import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/pages/dashboard/components/DashboardLayout";
import { supabase } from "@/lib/supabase";
import { useAuthGuard } from "@/hooks/useAuthGuard";

interface Lead {
  id: string;
  state: string;
  city: string;
  zip: string;
  bill_range: string;
  solar_score: number;
  lead_score: number;
  timeline: string;
  price: number;
  status: "available" | "reserved" | "sold";
}

const STATES = [
  "All States","Alabama","Alaska","Arizona","Arkansas","California","Colorado",
  "Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois",
  "Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland",
  "Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana",
  "Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York",
  "North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah",
  "Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

function ScoreBadge({ score, type }: { score: number; type: "lead" | "solar" }) {
  const color =
    score >= 90 ? "bg-green-100 text-green-700"
    : score >= 75 ? "bg-amber-100 text-amber-700"
    : "bg-red-100 text-red-600";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      {type === "lead" ? <i className="ri-user-star-line"></i> : <i className="ri-sun-line"></i>}
      {score}
    </span>
  );
}

function BuyModal({
  lead,
  onClose,
  onSuccess,
}: {
  lead: Lead;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [step, setStep] = useState<"confirm" | "reserving" | "redirecting" | "error">("confirm");
  const [errorMsg, setErrorMsg] = useState("");
  const [reservedUntil, setReservedUntil] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(300);

  useEffect(() => {
    if (step !== "redirecting" || !reservedUntil) return;
    const interval = setInterval(() => {
      const secs = Math.max(0, Math.floor((reservedUntil.getTime() - Date.now()) / 1000));
      setCountdown(secs);
      if (secs === 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [step, reservedUntil]);

  const handleBuy = async () => {
    setStep("reserving");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      // Step 1: Reserve the lead
      const reserveRes = await fetch(
        "https://ukfoujowiwsrrmajzajy.supabase.co/functions/v1/reserve-lead",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ lead_id: lead.id }),
        }
      );
      const reserveData = await reserveRes.json();
      if (!reserveRes.ok) throw new Error(reserveData.error || "Failed to reserve lead");

      setReservedUntil(new Date(reserveData.reserved_until));
      setStep("redirecting");

      // Step 2: Create Stripe Checkout Session
      const checkoutRes = await fetch(
        "https://ukfoujowiwsrrmajzajy.supabase.co/functions/v1/create-checkout-session",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            lead_id: lead.id,
            success_url: `${window.location.origin}/dashboard/purchased?success=true`,
            cancel_url: `${window.location.origin}/dashboard?cancelled=true`,
          }),
        }
      );
      const checkoutData = await checkoutRes.json();
      if (!checkoutRes.ok) throw new Error(checkoutData.error || "Failed to create checkout");

      // Redirect to Stripe
      window.location.href = checkoutData.url;
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong");
      setStep("error");
    }
  };

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Purchase Lead</h3>
          {step === "confirm" && (
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>
          )}
        </div>

        {step === "error" && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
            <div>
              <p className="font-semibold mb-1">Unable to purchase lead</p>
              <p>{errorMsg}</p>
            </div>
          </div>
        )}

        <div className="bg-gray-50 rounded-xl p-4 space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-semibold">{lead.city}, {lead.state} {lead.zip}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Monthly Bill</span><span className="font-semibold">{lead.bill_range}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Lead Score</span><ScoreBadge score={lead.lead_score} type="lead" /></div>
          <div className="flex justify-between"><span className="text-gray-500">Solar Score</span><ScoreBadge score={lead.solar_score} type="solar" /></div>
          <div className="flex justify-between"><span className="text-gray-500">Timeline</span><span className="font-semibold">{lead.timeline}</span></div>
          <div className="border-t border-gray-200 pt-3 flex justify-between">
            <span className="font-bold text-gray-900">Total</span>
            <span className="font-bold text-[#16a34a] text-lg">${lead.price}</span>
          </div>
        </div>

        {step === "redirecting" && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700 flex items-center gap-2">
            <i className="ri-time-line flex-shrink-0"></i>
            <span>Lead reserved! Redirecting to Stripe... <strong>{mins}:{secs.toString().padStart(2, "0")} remaining</strong></span>
          </div>
        )}

        {step === "confirm" && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700 flex items-start gap-2">
            <i className="ri-time-line mt-0.5 flex-shrink-0"></i>
            <span>This lead will be <strong>reserved for 5 minutes</strong> while you complete checkout. Full contact details unlock after payment.</span>
          </div>
        )}

        <div className="flex gap-3">
          {(step === "confirm" || step === "error") && (
            <>
              <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition cursor-pointer whitespace-nowrap">
                Cancel
              </button>
              <button
                onClick={handleBuy}
                disabled={step === "reserving"}
                className="flex-1 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white py-2.5 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-secure-payment-line mr-1"></i>Pay ${lead.price}
              </button>
            </>
          )}
          {(step === "reserving" || step === "redirecting") && (
            <div className="flex-1 flex items-center justify-center gap-2 text-sm text-gray-500 py-2.5">
              <i className="ri-loader-4-line animate-spin"></i>
              {step === "reserving" ? "Reserving lead..." : "Redirecting to Stripe..."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AvailableLeadsPage() {
  const { loading: authLoading } = useAuthGuard();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("All States");
  const [priceFilter, setPriceFilter] = useState("all");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const cancelled = searchParams.get("cancelled") === "true";

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from("leads")
      .select("id, state, city, zip, bill_range, solar_score, lead_score, timeline, price, status, reserved_until")
      .or(`status.eq.available,and(status.eq.reserved,reserved_until.lt.${now})`)
      .eq("is_premium", false)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setLeads(data as Lead[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    fetchLeads();
    const interval = setInterval(fetchLeads, 30000);
    return () => clearInterval(interval);
  }, [fetchLeads, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#16a34a]"></i>
          <span className="text-sm">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const filtered = leads.filter((l) => {
    if (stateFilter !== "All States" && l.state !== stateFilter) return false;
    if (priceFilter === "under150" && l.price >= 150) return false;
    if (priceFilter === "150to175" && (l.price < 150 || l.price > 175)) return false;
    if (priceFilter === "over175" && l.price <= 175) return false;
    if (scoreFilter === "high" && l.lead_score < 90) return false;
    if (scoreFilter === "mid" && (l.lead_score < 75 || l.lead_score >= 90)) return false;
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Available Leads</h1>
            <p className="text-gray-500 text-sm mt-1">
              {loading ? "Loading..." : `${filtered.length} exclusive leads available`}
            </p>
          </div>
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 px-4 py-2 rounded-full">
            <div className="w-2 h-2 bg-[#16a34a] rounded-full animate-pulse"></div>
            <span className="text-xs font-medium text-green-700">Live marketplace</span>
          </div>
        </div>

        {cancelled && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center gap-3 text-amber-800 text-sm">
            <i className="ri-information-line text-lg"></i>
            Payment cancelled. The lead has been released back to the marketplace.
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer"
          >
            {STATES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select
            value={priceFilter}
            onChange={(e) => setPriceFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer"
          >
            <option value="all">All Prices</option>
            <option value="under150">Under $150</option>
            <option value="150to175">$150–$175</option>
            <option value="over175">Over $175</option>
          </select>
          <select
            value={scoreFilter}
            onChange={(e) => setScoreFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer"
          >
            <option value="all">All Scores</option>
            <option value="high">High (90+)</option>
            <option value="mid">Mid (75–89)</option>
          </select>
          <button
            onClick={fetchLeads}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 transition cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-refresh-line"></i> Refresh
          </button>
        </div>

        {/* Lead cards */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 animate-pulse">
                <div className="h-4 bg-gray-100 rounded w-2/3 mb-3"></div>
                <div className="h-3 bg-gray-100 rounded w-1/3 mb-4"></div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="h-12 bg-gray-100 rounded-lg"></div>
                  <div className="h-12 bg-gray-100 rounded-lg"></div>
                </div>
                <div className="h-8 bg-gray-100 rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((lead, idx) => {
              // Deterministic urgency signals per lead (seeded by index so they're stable)
              const viewerCount = 1 + ((lead.lead_score + idx) % 3);
              const isHot = lead.lead_score >= 88;
              const isNew = idx < 3;
              return (
              <div
                key={lead.id}
                className={`bg-white border rounded-2xl p-5 hover:border-green-200 transition-all relative ${isHot ? "border-amber-200 ring-1 ring-amber-100" : "border-gray-100"}`}
              >
                {/* Badges */}
                <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                  {isHot && (
                    <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-fire-line"></i> Hot Lead
                    </span>
                  )}
                  {isNew && !isHot && (
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                      New
                    </span>
                  )}
                </div>

                <div className="flex items-start justify-between mb-4 pr-16">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-map-pin-line text-[#16a34a] text-sm"></i>
                      <span className="font-bold text-gray-900 text-sm">{lead.city}, {lead.state}</span>
                    </div>
                    <span className="text-xs text-gray-400">ZIP {lead.zip}</span>
                  </div>
                  <span className="text-xl font-bold text-[#16a34a]">${lead.price}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <p className="text-gray-400 mb-1">Monthly Bill</p>
                    <p className="font-semibold text-gray-800">{lead.bill_range}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <p className="text-gray-400 mb-1">Timeline</p>
                    <p className="font-semibold text-gray-800">{lead.timeline}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Lead</span>
                    <ScoreBadge score={lead.lead_score} type="lead" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Solar</span>
                    <ScoreBadge score={lead.solar_score} type="solar" />
                  </div>
                  {/* Live viewer count */}
                  <div className="ml-auto flex items-center gap-1 text-xs text-gray-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block"></span>
                    {viewerCount} viewing
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <i className="ri-eye-off-line"></i>
                    <span>Contact hidden until purchase</span>
                  </div>
                  <button
                    onClick={() => setSelectedLead(lead)}
                    className={`text-white px-4 py-2 rounded-lg text-xs font-semibold transition cursor-pointer whitespace-nowrap ${isHot ? "bg-amber-500 hover:bg-amber-600" : "bg-[#16a34a] hover:bg-[#15803d]"}`}
                  >
                    Buy Lead
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-search-line text-4xl mb-3 block"></i>
            <p className="font-medium">No leads match your filters</p>
            <p className="text-sm mt-1">Try adjusting your state or score filters</p>
          </div>
        )}
      </div>

      {selectedLead && (
        <BuyModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onSuccess={() => {
            setSelectedLead(null);
            navigate("/dashboard/purchased");
          }}
        />
      )}
    </DashboardLayout>
  );
}
